import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Icon } from '@lomarengas/design-system';
import {
  DataTable,
  DataTableItem,
  PaginationComponent,
  SearchBar,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getPlacesAsync,
  PlacesFilter,
  selectPlaceItems,
  selectPlaceItemsLoadingStatus,
  selectPlaceItemsPagination,
} from './placesSlice';
import { LOADING_STATUS } from '../../common/constants';
import { InitialPaginationParameters } from '../../common/types/pagination.types';

function Places() {
  const dispatch = useAppDispatch();
  const placeItems = useAppSelector(selectPlaceItems);
  const placeItemsPagination = useAppSelector(selectPlaceItemsPagination);
  const placeItemsLoadingStatus = useAppSelector(selectPlaceItemsLoadingStatus);

  const initialPlacesFilter: PlacesFilter = {
    PageNumber: InitialPaginationParameters.PageNumber,
    PageSize: InitialPaginationParameters.PageSize,
    searchString: '',
  };

  const [placesFilter, setPlacesFilter] = useState(initialPlacesFilter);
  const [placesTableItems, setPlacesTableItems] = useState<DataTableItem[]>([]);

  const handleInputChange = event => {
    const { target } = event;
    const { value, name } = target;
    setPlacesFilter({ ...placesFilter, [name]: value });
  };

  const doSearch = () => {
    placesFilter.PageNumber = InitialPaginationParameters.PageNumber;
    dispatch(getPlacesAsync(placesFilter));
  };

  const HandlePaginationClick = (Page: number) => {
    const paginatedFiltering = placesFilter;
    paginatedFiltering.PageNumber = Page;
    setPlacesFilter(paginatedFiltering);
    dispatch(getPlacesAsync(paginatedFiltering));
  };

  useEffect(() => {
    doSearch();
  }, []);

  useEffect(() => {
    if (placeItems && placeItems.length > 0) {
      const formattedPlaceItemsList: DataTableItem[] = [];
      placeItems?.forEach(item => {
        formattedPlaceItemsList.push({
          ItemId: item.id,
          Items: [item.id, item.name, item.showSearchSuggestion ? '✔️' : ''],
          Url: `/locations/places/${item.id}`,
        });
      });
      setPlacesTableItems(formattedPlaceItemsList);
    }
  }, [placeItems]);

  const AddNewButton: JSX.Element = (
    <Link to="/locations/places/addnew" className="btn btn-blue ps-16">
      <Icon CssClass="me-8" AriaLabel="Uusi" Name="plus" />
      Lisää uusi
    </Link>
  );

  return (
    <>
      <Helmet>
        <title>Sijaintitiedot - Paikat - Haku</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Sijaintitiedot']}
          Children={AddNewButton}
          Title="Paikat"
        />

        <SearchBar
          FreeTextOnChangeHandler={handleInputChange}
          FreeTextPlaceholder="Hae..."
          SearchHandler={doSearch}
          IsLoading={placeItemsLoadingStatus !== LOADING_STATUS.IDLE}
          Value={placesFilter.searchString}
        />

        {placeItemsLoadingStatus === LOADING_STATUS.IDLE &&
          placeItems !== null &&
          placeItems.length > 0 && (
            <>
              <DataTable
                ColumnWidths={[220, 220, 20]}
                Header={['Paikan ID', 'Paikan nimi', 'Hakuehdotus']}
                Items={placesTableItems}
                LegendText="paikkaa"
                TotalItems={placeItemsPagination.totalCount}
              />
              <PaginationComponent
                OnPageClick={HandlePaginationClick}
                PaginationData={placeItemsPagination}
              />
            </>
          )}

        {placeItemsLoadingStatus === LOADING_STATUS.IDLE &&
          placeItems != null &&
          placeItems.length < 1 && <p>Ei paikkoja annetuilla hakuehdoilla.</p>}
      </Container>
    </>
  );
}

export default Places;
